/**
 * GoalDeleteSheet — bottom sheet confirmation for goal deletion.
 * Replaces window.confirm() with branded, accessible UI.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Trash2 } from 'lucide-react'

interface GoalDeleteSheetProps {
  isOpen: boolean
  goalName: string
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const GoalDeleteSheet: React.FC<GoalDeleteSheetProps> = ({
  isOpen,
  goalName,
  isPending,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[2px]"
            onClick={onCancel}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="fixed inset-x-0 bottom-0 z-[71] rounded-t-2xl bg-[var(--color-surface)] border-t border-white/[0.08] px-5 pt-4 pb-safe"
          >
            {/* Drag handle */}
            <div className="w-8 h-1 rounded-full bg-white/[0.12] mx-auto mb-5" />

            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-2xl bg-danger/[0.1] flex items-center justify-center mb-4">
                <Trash2 size={20} className="text-danger" />
              </div>

              <h3 className="text-[15px] font-display font-bold text-text mb-2">
                {t('goals.deleteTitle')}
              </h3>

              <p className="text-[13px] text-text-hint leading-relaxed px-3">
                <span className="font-semibold text-text">"{goalName}"</span>
                {' '}{t('goals.deleteDesc')}
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={onCancel}
                disabled={isPending}
                className="flex-1 h-12 rounded-xl bg-white/[0.06] border border-white/[0.08] text-sm font-medium text-text-hint hover:bg-white/[0.09] transition-colors tap-highlight-transparent disabled:opacity-40"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={onConfirm}
                disabled={isPending}
                className="flex-1 h-12 rounded-xl bg-danger text-white text-sm font-semibold disabled:opacity-50 transition-opacity tap-highlight-transparent"
              >
                {isPending ? '...' : t('goals.delete')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
